import { useState } from 'react'

interface Props {
  isOpen: boolean
  onConsent: () => void
  onDecline: () => void
}

export function DataProcessingConsentModal({ isOpen, onConsent, onDecline }: Props) {
  const [agreed, setAgreed] = useState(false)

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="relative max-w-2xl w-full mx-4 bg-app-surface border border-app-line rounded-xl shadow-2xl">
        {/* Header */}
        <div className="border-b border-app-line px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-sky-100 dark:bg-app-highlight flex items-center justify-center">
              <svg className="w-6 h-6 text-sky-600 dark:text-app-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-app-ink font-display">Data Processing Consent</h2>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6 space-y-4 max-h-[60vh] overflow-y-auto">
          <div className="space-y-3 text-app-ink">
            <p>
              The Resume Analysis feature uses artificial intelligence to evaluate your resume. 
              To provide the best quality service, we need to process your personal data.
            </p>

            <div className="bg-sky-50 dark:bg-app-subtle/50 border border-sky-200 dark:border-app-line p-4 space-y-3 rounded-2xl shadow-app-card">
              <h3 className="font-semibold text-app-ink flex items-center gap-2 font-display">
                <svg className="w-5 h-5 text-sky-600 dark:text-app-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                What data we process:
              </h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-sky-600 dark:text-app-accent mt-0.5">•</span>
                  <span>Content of your resume (including personal data, work experience, education)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-sky-600 dark:text-app-accent mt-0.5">•</span>
                  <span>Analysis results and recommendations</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-sky-600 dark:text-app-accent mt-0.5">•</span>
                  <span>File metadata (name, size, upload date)</span>
                </li>
              </ul>
            </div>

            <div className="bg-emerald-50 dark:bg-app-subtle/50 border border-emerald-200 dark:border-app-line p-4 space-y-3 rounded-2xl shadow-app-card">
              <h3 className="font-semibold text-app-ink flex items-center gap-2 font-display">
                <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Your rights:
              </h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 dark:text-emerald-400 mt-0.5">•</span>
                  <span>Data is not used to train AI models</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 dark:text-emerald-400 mt-0.5">•</span>
                  <span>We store analyses for 90 days, unless you request earlier deletion</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 dark:text-emerald-400 mt-0.5">•</span>
                  <span>You can delete your resume and analyses at any time</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 dark:text-emerald-400 mt-0.5">•</span>
                  <span>Data is encrypted and stored securely</span>
                </li>
              </ul>
            </div>

            <p className="text-sm">
              Data processing is carried out in accordance with GDPR and our{' '}
              <a
                href="https://www.iubenda.com/privacy-policy/14898698"
                target="_blank"
                rel="noopener noreferrer"
                className="text-app-accent hover:text-app-accent-hover underline"
              >
                Privacy Policy
              </a>
              .
            </p>
          </div>

          {/* Consent Checkbox */}
          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-1 w-5 h-5 border-app-line text-app-accent focus:ring-app-accent focus:ring-offset-0 bg-app-surface cursor-pointer rounded-lg"
            />
            <span className="text-sm text-app-ink group-hover:text-app-ink transition-colors">
              I consent to the processing of my personal data contained in my resume 
              for the purpose of conducting analysis and receiving recommendations.
            </span>
          </label>
        </div>

        {/* Footer */}
        <div className="border-t border-app-line px-6 py-4 flex gap-3 justify-end">
          <button
            onClick={onDecline}
            className="px-6 py-2.5 rounded-lg border border-app-line text-app-ink font-medium hover:bg-app-subtle transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConsent}
            disabled={!agreed}
            className={`px-6 py-2.5 rounded-lg font-medium transition-colors ${
              agreed
                ? 'bg-app-primary hover:bg-app-primary-hover text-white'
                : 'bg-app-subtle text-app-muted cursor-not-allowed'
            }`}
          >
            I Consent
          </button>
        </div>
      </div>
    </div>
  )
}
